import { put, takeEvery } from "redux-saga/effects";
import { ErrorResponse } from "../../../models/store";
import {
    Clip,
    Folder,
    Footage,
    Timeline,
    TimelineInfo,
    UploadTimelineInfo,
} from "../../models/Timeline";
import {
    GetRecentFootages,
    GetRecentFootagesComplete,
    GetUneditedFootagesComplete,
    getTimeline,
    GetTimelineComplete,
    GetTimelineFailed,
    getFootageList,
    getFootageListComplete,
    getFootageVideoComplete,
    getFootageVideo,
    getThumbnail,
    getThumbnailComplete,
    getUsableDirList,
    getUsableDirListComplete,
    CreateTimeline,
    CreateTimelineComplete,
    AddClipToTimeline,
    AddClipToTimelineComplete,
    ClipMoveStarted,
    ClipMoveComplete,
    DeleteClip,
    DeleteClipComplete,
    GetTimelines,
    GetTimelinesComplete,
    UploadTimeline,
    UploadTimelineComplete,
    CutClip,
    CutClipComplete,
    CutClipFailed,
    DeleteTimeline,
    DeleteTimelineComplete,
    UploadTimelineFailed,
    UpdateTimelineStatus,
    UpdateTimelineStatusCompleted,
    EditTimelineClip,
    EditClipComplete,
    RenameTimelineComplete,
    RenameTimeline,
    CreateTimelineFailed,
    UpdateTimelineClipList,
    UpdateTimelineClipListComplete,
} from "../actions/TimelineActions";
import TimelineApi from "../apis/TimelineApi";
import TimelineEvents from "../events/TimelineEvents";

// 未編集のフッテージ一覧取得
function* GetUneditedFootagesWorker() {
    const footageList: Footage[] = yield TimelineApi.getUneditedFootages();
    yield put(GetUneditedFootagesComplete(footageList));
}

// タイムライン取得
function* GetTimelinesWoker({ statusList }: ReturnType<typeof GetTimelines>) {
    const timelineList: TimelineInfo[] = yield TimelineApi.getTimelines(statusList);
    yield put(GetTimelinesComplete(timelineList));
}

// タイムラインをcineonhubにアップロード
function* UploadTimelineWoker({ timeline }: ReturnType<typeof UploadTimeline>) {
    const response: UploadTimelineInfo | ErrorResponse = yield TimelineApi.UploadTimeline(timeline);
    if (response instanceof ErrorResponse) {
        if (response && response.response.status !== 200) {
            yield put(UploadTimelineFailed());
        }
    } else {
        yield put(UploadTimelineComplete(response));
    }
}

// タイムラインのアップロードキャンセル
function* UpdateTimelineStatusWoker({ status, id }: ReturnType<typeof UpdateTimelineStatus>) {
    const timeline: TimelineInfo = yield TimelineApi.UpdateTimelineStatus(status, id);
    yield put(UpdateTimelineStatusCompleted(timeline));
}

// 直近のフッテージ取得
function* GetRecentFootagesWorker({ userId, days }: ReturnType<typeof GetRecentFootages>) {
    const footageList: Footage[] = yield TimelineApi.getRecentFootages(userId, days);
    yield put(GetRecentFootagesComplete(footageList));
}

// タイムライン作成
function* CreateTimelineWorker({ userId, timeLineName }: ReturnType<typeof CreateTimeline>) {
    const result: TimelineInfo | ErrorResponse = yield TimelineApi.createTimeline(
        userId,
        timeLineName
    );
    if (result instanceof ErrorResponse) {
        if (result.response && result.response.status !== 200) {
            const timelineList: TimelineInfo[] = yield TimelineApi.getTimelines([]);
            yield put(CreateTimelineFailed(result.stack, timelineList));
        }
    } else {
        const createTimeline: Timeline = {
            timeline: {
                id: result.id,
                name: result.name,
                status: result.status,
            },
            clipList: [],
        };
        yield put(CreateTimelineComplete(createTimeline));
    }
}

// タイムライン削除
function* DeleteTimelineWorker({ userId, timelineId }: ReturnType<typeof DeleteTimeline>) {
    yield TimelineApi.deleteTimeline(userId, timelineId);
    yield put(DeleteTimelineComplete(timelineId));
}

// タイムライン取得
function* getTimelineWorker({ id }: ReturnType<typeof getTimeline>) {
    const result: Timeline = yield TimelineApi.getTimeline(id);
    if (result && result.timeline) {
        // 取得成功
        yield put(GetTimelineComplete(result));
    } else {
        // 取得失敗
        yield put(GetTimelineFailed(result));
    }
}

// フッテージリストを取得する
function* getFootageWorker({ id }: ReturnType<typeof getFootageList>) {
    const list: Footage[] = yield TimelineApi.getFootageList(id);
    // const result: Footage[] = [];
    // list.footageList.forEach(async (row: Footage) => {
    //     const imageUrl = await TimelineApi.getThumbnail(row.id);
    //     result.push({ id: row.id, name: row.name, length: row.length, imageUrl: imageUrl });z
    // });
    yield put(getFootageListComplete(list));
}

//フッテージ動画取得
function* getFootageVideoWorker({ id }: ReturnType<typeof getFootageVideo>) {
    const result: string = yield TimelineApi.getFootageVideo(id);
    yield put(getFootageVideoComplete(result, id));
}

//サムネイル取得
function* getThumbnailWorker({ id }: ReturnType<typeof getThumbnail>) {
    const result: string = yield TimelineApi.getThumbnail(id);
    yield put(getThumbnailComplete(result));
}

//利用可能ディレクトリリスト取得
function* getUsableDirListWorker({ id }: ReturnType<typeof getUsableDirList>) {
    const result: Folder[] = yield TimelineApi.getUsableDirList(id);
    yield put(getUsableDirListComplete(result));
}

// ディレクトリ作成
function* CreateClipWorker({
    timelineId,
    footageId,
    start,
    end,
    seq,
    userId,
}: ReturnType<typeof AddClipToTimeline>) {
    const clip: Clip[] = yield TimelineApi.createClip(
        timelineId,
        footageId,
        start,
        end,
        seq,
        userId
    );
    yield put(AddClipToTimelineComplete(clip));
}

function* ClipMoveWorker({ id, seq, userId }: ReturnType<typeof ClipMoveStarted>) {
    const clip: Clip[] = yield TimelineApi.moveClip(id, seq, userId);
    yield put(ClipMoveComplete(clip));
}

function* DeleteClipWorker({ id, userId }: ReturnType<typeof DeleteClip>) {
    const clip: Clip[] = yield TimelineApi.deleteClip(id, userId);
    yield put(DeleteClipComplete(clip));
}

function* CutClipWorker({ id, cutPoint, userId }: ReturnType<typeof CutClip>) {
    const clip: Clip[] = yield TimelineApi.cutClip(id, cutPoint, userId);
    if (clip) {
        yield put(CutClipComplete(clip));
    } else {
        // 失敗処理は今後対応する予定
        // エラー
        yield put(CutClipFailed(clip));
    }
}

function* EditClipWorker({
    deleteClipId,
    timelineId,
    footageId,
    start,
    end,
    seq,
    userId,
}: ReturnType<typeof EditTimelineClip>) {
    const deletedList: Clip = yield TimelineApi.deleteClip(deleteClipId, userId);
    let clipList: Clip[] = [];

    // 削除成功する時
    if (deletedList) {
        const clip: Clip[] = yield TimelineApi.createClip(
            timelineId,
            footageId,
            start,
            end,
            seq,
            userId
        );
        clipList = clip;
        yield put(EditClipComplete(clipList));
    } else {
        // 失敗処理は今後対応する予定
        console.log("編集失敗");
    }
}

// タイライム名変更
function* RenameTimelineWorker({ id, name, userId }: ReturnType<typeof RenameTimeline>) {
    const timelines: TimelineInfo[] = yield TimelineApi.renameTimeline(id, name, userId);
    yield put(RenameTimelineComplete(timelines));
}

// タイムライン編集のUNDO、REDO
function* UpdateTimelineClipListWorker({
    timelineId,
    userId,
    clipList,
}: ReturnType<typeof UpdateTimelineClipList>) {
    const timelines: Clip[] = yield TimelineApi.updateTimelineClipList(
        timelineId,
        userId,
        clipList
    );
    yield put(UpdateTimelineClipListComplete(timelines));
}

export default function* TimelineSaga() {
    yield takeEvery(TimelineEvents.GetUneditedFootagesStarted, GetUneditedFootagesWorker);
    yield takeEvery(TimelineEvents.GetTimelinesStarted, GetTimelinesWoker);
    yield takeEvery(TimelineEvents.UploadTimelineStarted, UploadTimelineWoker);
    yield takeEvery(TimelineEvents.GetRecentFootagesStarted, GetRecentFootagesWorker);
    yield takeEvery(TimelineEvents.CreateTimelineStarted, CreateTimelineWorker);
    yield takeEvery(TimelineEvents.DeleteTimelineStarted, DeleteTimelineWorker);
    yield takeEvery(TimelineEvents.GetTimelineStarted, getTimelineWorker);
    yield takeEvery(TimelineEvents.GetFootageListStarted, getFootageWorker);
    yield takeEvery(TimelineEvents.GetFootageVideoStarted, getFootageVideoWorker);
    yield takeEvery(TimelineEvents.GetThumbnailStarted, getThumbnailWorker);
    yield takeEvery(TimelineEvents.GetUsableDirListStarted, getUsableDirListWorker);
    yield takeEvery(TimelineEvents.AddClipToTimelineStarted, CreateClipWorker);
    yield takeEvery(TimelineEvents.ClipMoveStarted, ClipMoveWorker);
    yield takeEvery(TimelineEvents.DeleteClipStarted, DeleteClipWorker);
    yield takeEvery(TimelineEvents.CutClipStarted, CutClipWorker);
    yield takeEvery(TimelineEvents.UpdateTimelineStatus, UpdateTimelineStatusWoker);
    yield takeEvery(TimelineEvents.EditClipStarted, EditClipWorker);
    yield takeEvery(TimelineEvents.RenameTimelineStarted, RenameTimelineWorker);
    yield takeEvery(TimelineEvents.UpdateTimelineClipList, UpdateTimelineClipListWorker);
}
